//@flow

import React from 'react';
import {Link} from 'react-router-dom';

import {colors} from '../../../../assets/styles/colors';
import {
  makeStyles,
  TableContainer,
  Table,
  TableHead,
  TableCell,
  Typography,
  TableBody,
  TableRow,
  Hidden,
  MenuItem,
} from '@material-ui/core';
import {useCrmCampaigns} from '../../utils';
import InlineLoader from '../../../../elements/LoadingSpinner/InlineLoader';
import {formatDate} from '../../../../utils/date';
import CustomButton from '../../../../elements/CustomButton/CustomButton';
import CustomChip from '../../../../elements/CustomChip';
import {Visibility} from '@material-ui/icons';
import {useCrmSegments} from '../../../crm-segment/utils';
import {Pagination} from '@material-ui/lab';
import CustomSelect from '../../../../elements/CustomSelect/CustomSelect';

const useStyles = makeStyles(theme => ({
  root: {
    position: 'relative',
    padding: '1rem',
  },
  tableHeader: {
    background: colors.grey300,
  },
  tableHeaderText: {
    fontWeight: 600,
    fontSize: '.9rem',
  },

  tableRow: {
    '&:nth-of-type(even)': {
      background: '#F8F9FB',
    },
  },

  tableContainer: {
    marginBottom: '1rem',
    position: 'relative',
    scrollBehavior: 'smooth',
    background: 'white',
    borderRadius: '.75rem',
    boxShadow:
      '0px 0.5px 2px 0px rgba(96, 97, 112, 0.16), 0px 0px 1px 0px rgba(40, 41, 61, 0.08);',
    minHeight: '50vh',

    '&::-webkit-scrollbar': {
      width: '5px',
      height: '5px',
    },

    '&::-webkit-scrollbar-track': {
      background: colors.grey200,
      borderRadius: '5px',
    },

    '&::-webkit-scrollbar-thumb': {
      background: colors.grey400,
      borderRadius: '5px',
    },
  },
  emptyTableContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '1rem',
    padding: '3rem 1rem',
    width: '100%',
  },
  loader: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 1,
    background: 'rgba(255, 255, 255, .8)',
  },
  actions: {
    display: 'flex',
    alignItems: 'center',
    gap: '.25rem',
  },
  paginationArea: {
    padding: '1rem',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    [theme.breakpoints.down('xs')]: {
      flexDirection: 'column',
    },
  },
}));

const getSegmentExecutionDate = (type: string, scheduledAt: string) => {
  const isManual = type === 'MANUAL';
  const date = new Date(scheduledAt);

  const dateFormat = isManual ? 'DD-MM-YYYY HH:mm A' : 'HH:mm A';

  return formatDate(date, dateFormat);
};

export default function CrmCampaignTable() {
  const classes = useStyles();

  const {
    crmCampaigns,
    loading,
    pagination,
    setPage,
    setSize,
  } = useCrmCampaigns();
  const {loading: loadingSegments, segments} = useCrmSegments({
    page: 0,
    size: 30,
  });

  return (
    <div className={classes.root}>
      <TableContainer className={classes.tableContainer}>
        {loading && loadingSegments && (
          <div className={classes.loader}>
            <InlineLoader loading size={32} />
          </div>
        )}
        <Table>
          <TableHead className={classes.tableHeader}>
            <TableCell>
              <Typography className={classes.tableHeaderText}>
                Nombre
              </Typography>
            </TableCell>
            <TableCell>
              <Typography className={classes.tableHeaderText}>
                Segmento
              </Typography>
            </TableCell>
            <TableCell>
              <Typography className={classes.tableHeaderText}>
                Fecha de ejecución
              </Typography>
            </TableCell>
            <TableCell>
              <Typography className={classes.tableHeaderText}>
                Estado
              </Typography>
            </TableCell>
            <TableCell>
              <Typography className={classes.tableHeaderText}>
                Acciones
              </Typography>
            </TableCell>
          </TableHead>

          <TableBody className={classes.tableBody}>
            {crmCampaigns
              .filter(campaign => campaign.state !== 'ARCHIVED')
              .map(campaign => {
                const segment = segments.find(
                  segment => segment.id === campaign.segmentId
                );

                return (
                  <TableRow key={campaign.id} className={classes.tableRow}>
                    <TableCell>{campaign.name}</TableCell>
                    <TableCell>
                      {segment?.name}{' '}
                      {segment && (
                        <CustomChip
                          size="small"
                          color={
                            segment.segmentType === 'MANUAL'
                              ? 'primary'
                              : 'secondary'
                          }
                          label={
                            segment.segmentType === 'MANUAL'
                              ? 'Manual'
                              : 'Dinámico'
                          }
                        />
                      )}
                    </TableCell>
                    <TableCell>
                      {segment &&
                        getSegmentExecutionDate(
                          segment?.segmentType,
                          campaign.scheduledAt
                        )}
                    </TableCell>
                    <TableCell>
                      <CustomChip
                        size="small"
                        color={
                          campaign.state === 'ACTIVE' ? 'primary' : 'warning'
                        }
                        label={
                          campaign.state === 'ACTIVE' ? 'Activo' : 'Borrador'
                        }
                      />
                    </TableCell>
                    <TableCell className={classes.actions}>
                      {/*      <CustomButton variant="outlined" endIcon={<Send />}>
                      Ejecutar
                    </CustomButton> */}

                      <CustomButton
                        variant="outlined"
                        endIcon={<Visibility />}
                        component={Link}
                        to={`/campaigns/detail/${campaign.id}`}
                      >
                        Detalle
                      </CustomButton>
                      {/*                   <IconButton
                      component={Link}
                      to={`/campaigns/detail/${campaign.id}`}
                    >
                      <ArrowForwardIos fontSize="small" />
                    </IconButton> */}
                    </TableCell>
                  </TableRow>
                );
              })}
          </TableBody>
        </Table>
        <div className={classes.paginationArea}>
          <Pagination
            count={
              pagination &&
              (pagination.size >= pagination.total
                ? 1
                : Math.ceil(pagination.total / pagination.size))
            }
            className={classes.pagination}
            color="primary"
            page={pagination.page + 1}
            onChange={(e, page) => {
              setPage(page - 1);
            }}
          />
          <Hidden smUp>
            <CustomSelect
              native
              pagination
              disableUnderline
              className={classes.sizeSelect}
              value={pagination.size}
              onChange={e => setSize(e.target.value)}
            >
              <option value={8}>8 por página</option>
              <option value={16}>16 por página</option>
              <option value={24}>24 por página</option>
              <option value={32}>32 por página</option>
            </CustomSelect>
          </Hidden>
          <Hidden xsDown>
            <CustomSelect
              pagination
              disableUnderline
              className={classes.sizeSelect}
              value={pagination.size}
              onChange={e => setSize(e.target.value)}
            >
              <MenuItem value={8}>8 por página</MenuItem>
              <MenuItem value={16}>16 por página</MenuItem>
              <MenuItem value={24}>24 por página</MenuItem>
              <MenuItem value={32}>32 por página</MenuItem>
            </CustomSelect>
          </Hidden>
        </div>
        {crmCampaigns.length === 0 && (
          <aside className={classes.emptyTableContainer}>
            <Typography variant="body1">No hay campañas</Typography>

            <CustomButton
              variant="outlined"
              component={Link}
              to="/campaigns/new"
            >
              Crear campaña
            </CustomButton>
          </aside>
        )}
      </TableContainer>
    </div>
  );
}
